import { type FormEvent, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useApi, useConsulta } from '@plataforma/web/api';
import { PublicacionAsistida, TareasCanales } from '../componentes/PublicacionAsistida';
import { fechaCorta, mxn } from '../formato';
import { usePuede } from '../permisos';
import type { Publicacion } from './comercial-comun';

interface Canal {
  clave: string;
  nombre: string;
  modo: 'api' | 'asistido';
  autos: boolean;
  piezas: boolean;
  codigo: string;
}
interface Cuenta {
  id: string;
  canal: string;
  nombreCanal: string;
  modo: 'api' | 'asistido';
  nombre: string;
  conCredenciales: boolean;
  referencia: Record<string, string>;
  plantilla: { titulo: string; descripcion: string } | null;
  ajustePrecio: string;
  automatico: 'nada' | 'autos' | 'piezas' | 'todo';
  grupos: string[];
  activa: boolean;
}
interface Externa {
  id: string;
  publicacionId: string;
  cuentaId: string;
  canal: string;
  nombreCanal: string;
  modo: string;
  destino: string | null;
  url: string | null;
  precioMxn: string;
  estado: string;
  error: string | null;
  creadoEn: string;
}
const AUTOMATICO: Record<string, string> = {
  nada: 'Solo cuando yo lo pida',
  autos: 'Autos en automático',
  piezas: 'Piezas en automático',
  todo: 'Todo en automático',
};
const CLASE_ESTADO: Record<string, string> = {
  publicada: 'exito',
  pausada: 'aviso',
  retirada: '',
  pendiente: 'aviso',
  error: 'peligro',
};

/**
 * Canales del lotero (arquitectura 2.15): cuentas propias (Mercado Libre, eBay, Meta y
 * WhatsApp por API; Marketplace, grupos, Craigslist y portales asistidos), plantilla y precio por
 * canal, qué sale en automático, publicación con un clic y tareas de los asistidos.
 */
export function Canales() {
  const api = useApi();
  const permitido = usePuede();
  const [params] = useSearchParams();
  const publicacionId = params.get('publicacion');
  const catalogo = useConsulta<Canal[]>('/channels/canales');
  const cuentas = useConsulta<Cuenta[]>('/channels/canales/cuentas');
  const publicaciones = useConsulta<Publicacion[]>('/commerce/publicaciones?estado=publicada');
  const externas = useConsulta<Externa[]>(
    publicacionId
      ? `/channels/canales/publicaciones?publicacionId=${publicacionId}`
      : '/channels/canales/publicaciones',
  );
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [nueva, setNueva] = useState(false);
  const [canalNuevo, setCanalNuevo] = useState('mercadolibre');
  const [elegidas, setElegidas] = useState<string[]>([]);
  const puedeCuentas = permitido('lotero.administrar');
  const publicacion = publicaciones.datos?.find((p) => p.id === publicacionId) ?? null;

  const hacer = async (fn: () => Promise<unknown>, mensaje: string) => {
    setError(null);
    setAviso(null);
    try {
      await fn();
      setAviso(mensaje);
      cuentas.recargar();
      externas.recargar();
      setNueva(false);
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const t = (f: FormData, k: string) => String(f.get(k) ?? '').trim();
  const guardar = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const canal = catalogo.datos?.find((c) => c.clave === t(f, 'canal'));
    const credenciales: Record<string, string> = {};
    for (const k of [
      'token',
      'tokenPagina',
      'catalogo',
      'pagina',
      'tipoCambio',
      'categoria',
      'marketplace',
      'ubicacion',
    ])
      if (t(f, `cred_${k}`)) credenciales[k] = t(f, `cred_${k}`);
    void hacer(
      () =>
        api('POST', '/channels/canales/cuentas', {
          canal: t(f, 'canal'),
          nombre: t(f, 'nombre'),
          ...(canal?.modo === 'api' && Object.keys(credenciales).length && { credenciales }),
          ...(t(f, 'plantillaTitulo') && {
            plantilla: {
              titulo: t(f, 'plantillaTitulo'),
              descripcion:
                t(f, 'plantillaDescripcion') ||
                '{descripcion}\n\nPrecio: {precio}\n{situacion}\nMás fotos: {enlace}',
            },
          }),
          ajustePrecio: t(f, 'ajustePrecio') || '0',
          automatico: t(f, 'automatico') || 'nada',
          grupos: t(f, 'grupos')
            .split('\n')
            .map((x) => x.trim())
            .filter(Boolean),
        }),
      'Cuenta conectada',
    );
  };
  const cambiarAutomatico = (c: Cuenta, automatico: string) =>
    hacer(() => api('PUT', `/channels/canales/cuentas/${c.id}`, { automatico }), 'Guardado');
  const desconectar = (c: Cuenta) => {
    if (window.confirm(`¿Desconectar «${c.nombre}»? Se borran sus llaves.`))
      void hacer(() => api('DELETE', `/channels/canales/cuentas/${c.id}`), 'Cuenta desconectada');
  };
  const publicar = () => {
    if (!publicacionId || !elegidas.length) return;
    void hacer(
      () => api('POST', '/channels/canales/publicaciones', { publicacionId, cuentas: elegidas }),
      'Enviado a los canales',
    );
  };
  const canalDe = (clave: string) => catalogo.datos?.find((c) => c.clave === clave);

  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Canales</h1>
          <div className="subtitulo">
            Tus cuentas, páginas y grupos: publica con un clic y retira al vender
          </div>
        </div>
        <div className="relleno" />
        {puedeCuentas && (
          <button className="boton primario" onClick={() => setNueva((x) => !x)}>
            {nueva ? 'Cancelar' : 'Conectar cuenta'}
          </button>
        )}
      </div>
      {error && <p className="error">{error}</p>}
      {aviso && <p className="etiqueta exito">{aviso}</p>}
      {nueva && (
        <form className="seccion cuerpo formulario" onSubmit={guardar}>
          <label className="campo">
            <span>Canal</span>
            <select name="canal" value={canalNuevo} onChange={(e) => setCanalNuevo(e.target.value)}>
              {(catalogo.datos ?? []).map((c) => (
                <option key={c.clave} value={c.clave}>
                  {c.nombre} ({c.modo === 'api' ? 'API' : 'asistido'})
                </option>
              ))}
            </select>
          </label>
          <label className="campo">
            <span>Nombre de la cuenta</span>
            <input
              name="nombre"
              required
              maxLength={120}
              placeholder="Mi cuenta de Mercado Libre"
            />
          </label>
          {canalDe(canalNuevo)?.modo === 'api' && (
            <>
              <label className="campo">
                <span>Token de acceso (se guarda cifrado)</span>
                <input name="cred_token" type="password" required autoComplete="off" />
              </label>
              {canalNuevo === 'meta' || canalNuevo === 'whatsapp' ? (
                <>
                  <label className="campo">
                    <span>Id del catálogo</span>
                    <input name="cred_catalogo" />
                  </label>
                  <label className="campo">
                    <span>Id de la página (opcional)</span>
                    <input name="cred_pagina" />
                  </label>
                  <label className="campo">
                    <span>Token de la página (opcional)</span>
                    <input name="cred_tokenPagina" type="password" autoComplete="off" />
                  </label>
                </>
              ) : canalNuevo === 'ebay' ? (
                <>
                  <label className="campo">
                    <span>Tipo de cambio MXN→USD</span>
                    <input name="cred_tipoCambio" inputMode="decimal" required />
                  </label>
                  <label className="campo">
                    <span>Marketplace (EBAY_MOTORS_US)</span>
                    <input name="cred_marketplace" defaultValue="EBAY_MOTORS_US" />
                  </label>
                  <label className="campo">
                    <span>Ubicación del inventario (merchantLocationKey)</span>
                    <input name="cred_ubicacion" />
                  </label>
                </>
              ) : (
                <label className="campo">
                  <span>Categoría (opcional)</span>
                  <input name="cred_categoria" placeholder="MLM1744" />
                </label>
              )}
            </>
          )}
          {canalDe(canalNuevo)?.modo === 'asistido' && (
            <label className="campo" style={{ gridColumn: '1 / -1' }}>
              <span>Grupos, páginas o portales (uno por renglón)</span>
              <textarea
                name="grupos"
                rows={3}
                placeholder={'Autos usados Monterrey\nYonkes de Nuevo León'}
              />
            </label>
          )}
          <label className="campo">
            <span>Ajuste de precio (%)</span>
            <input
              name="ajustePrecio"
              inputMode="decimal"
              defaultValue="0"
              placeholder="5 para absorber la comisión"
            />
          </label>
          <label className="campo">
            <span>Publicar en automático</span>
            <select name="automatico" defaultValue="nada">
              {Object.entries(AUTOMATICO).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          <label className="campo">
            <span>Plantilla del título (opcional)</span>
            <input name="plantillaTitulo" placeholder="{titulo} · {ciudad}" />
          </label>
          <label className="campo" style={{ gridColumn: '1 / -1' }}>
            <span>
              Plantilla de la descripción (variables:{' '}
              {
                '{titulo} {descripcion} {precio} {anio} {marca} {modelo} {ciudad} {situacion} {enlace}'
              }
              )
            </span>
            <textarea name="plantillaDescripcion" rows={3} />
          </label>
          <div className="acciones">
            <button className="boton primario">Conectar</button>
          </div>
        </form>
      )}

      <section className="seccion">
        <header>
          <h2>Mis cuentas</h2>
        </header>
        {cuentas.error && (
          <p className="error" style={{ margin: 16 }}>
            {cuentas.error}
          </p>
        )}
        {cuentas.datos && !cuentas.datos.length ? (
          <p className="vacio">
            Sin cuentas todavía. Conecta Mercado Libre, eBay o Meta por API, o agrega tus grupos de
            Facebook y portales como canales asistidos.
          </p>
        ) : (
          <div className="tarjetas">
            {(cuentas.datos ?? []).map((c) => (
              <article key={c.id} className="tarjeta" style={{ opacity: c.activa ? 1 : 0.5 }}>
                <div className="rotulo">
                  {c.nombreCanal} · {c.modo === 'api' ? 'API' : 'asistido'}
                </div>
                <strong>{c.nombre}</strong>
                <div className="nota">
                  {c.modo === 'api'
                    ? c.conCredenciales
                      ? 'Con llaves'
                      : 'Sin llaves'
                    : `${c.grupos.length} destinos`}
                  {Number(c.ajustePrecio)
                    ? ` · precio ${Number(c.ajustePrecio) > 0 ? '+' : ''}${c.ajustePrecio} %`
                    : ''}
                </div>
                {puedeCuentas ? (
                  <select
                    value={c.automatico}
                    onChange={(e) => void cambiarAutomatico(c, e.target.value)}
                    aria-label="Automático"
                  >
                    {Object.entries(AUTOMATICO).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="etiqueta">{AUTOMATICO[c.automatico]}</span>
                )}
                {puedeCuentas && c.activa && (
                  <div className="acciones">
                    <button className="boton" onClick={() => desconectar(c)}>
                      Desconectar
                    </button>
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="seccion">
        <header>
          <h2>{publicacion ? `Publicar «${publicacion.titulo}»` : 'Publicar en mis canales'}</h2>
          <div style={{ flex: 1 }} />
          {!publicacionId && (
            <span className="subtitulo">Elige una publicación desde Publicaciones</span>
          )}
        </header>
        {publicacion && (
          <div className="cuerpo" style={{ display: 'grid', gap: 12 }}>
            <div className="subtitulo">
              {mxn(publicacion.precioMxn)} · <Link to="/publicaciones">volver a Publicaciones</Link>
            </div>
            <div className="acciones" style={{ flexWrap: 'wrap' }}>
              {(cuentas.datos ?? [])
                .filter((c) => c.activa)
                .map((c) => (
                  <label
                    key={c.id}
                    className="campo"
                    style={{ flexDirection: 'row', alignItems: 'center' }}
                  >
                    <input
                      type="checkbox"
                      style={{ minHeight: 0 }}
                      checked={elegidas.includes(c.id)}
                      onChange={(e) =>
                        setElegidas((x) =>
                          e.target.checked ? [...x, c.id] : x.filter((y) => y !== c.id),
                        )
                      }
                    />
                    <span>{c.nombre}</span>
                  </label>
                ))}
              <button className="boton primario" disabled={!elegidas.length} onClick={publicar}>
                Publicar en {elegidas.length || ''} cuentas
              </button>
            </div>
            <h3 style={{ margin: 0 }}>Listo para copiar (canales asistidos)</h3>
            <PublicacionAsistida publicacionId={publicacion.id} />
          </div>
        )}
        {externas.error && (
          <p className="error" style={{ margin: 16 }}>
            {externas.error}
          </p>
        )}
        {externas.datos && externas.datos.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>Canal</th>
                <th>Dónde</th>
                <th className="num">Precio</th>
                <th>Estado</th>
                <th>Desde</th>
              </tr>
            </thead>
            <tbody>
              {externas.datos.map((x) => (
                <tr key={x.id}>
                  <td>{x.nombreCanal}</td>
                  <td>
                    {x.url ? (
                      <a href={x.url} target="_blank" rel="noreferrer">
                        {x.destino ?? x.url}
                      </a>
                    ) : (
                      (x.destino ?? '—')
                    )}
                    {!publicacionId && (
                      <div className="nota">
                        <Link to={`/canales?publicacion=${x.publicacionId}`}>
                          publicación {x.publicacionId.slice(-6)}
                        </Link>
                      </div>
                    )}
                  </td>
                  <td className="num">{mxn(x.precioMxn)}</td>
                  <td>
                    <span className={`etiqueta ${CLASE_ESTADO[x.estado] ?? ''}`}>{x.estado}</span>
                    {x.error && <div className="nota">{x.error}</div>}
                  </td>
                  <td>{fechaCorta(x.creadoEn)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="seccion">
        <header>
          <h2>Tareas pendientes</h2>
          <span className="subtitulo">
            Publicar donde falta y retirar al vender (sin doble venta)
          </span>
        </header>
        <div className="cuerpo">
          <TareasCanales {...(publicacionId && { publicacionId })} />
        </div>
      </section>
    </>
  );
}
